import { useState, useEffect } from 'react';
import { Package, ShoppingCart, Users, TrendingUp, DollarSign, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AdminLayout } from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await adminApi.getDashboard();
      setStats(res.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: stats ? `KES ${stats.total_revenue.toLocaleString()}` : '—',
      icon: DollarSign,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-900/20',
    },
    {
      title: 'Total Orders',
      value: stats?.total_orders || 0,
      icon: ShoppingCart,
      color: 'text-blue-400',
      bgColor: 'bg-blue-900/20',
    },
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'text-purple-400',
      bgColor: 'bg-purple-900/20',
    },
    {
      title: 'Customers',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'text-orange-400',
      bgColor: 'bg-orange-900/20',
    },
  ];

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    processing: 'bg-blue-500/20 text-blue-400',
    shipped: 'bg-purple-500/20 text-purple-400',
    delivered: 'bg-green-500/20 text-green-400',
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-stone-400 mt-1">Welcome back! Here's your store overview.</p>
          </div>
          {stats?.pending_orders > 0 && (
            <Badge className="bg-yellow-500/20 text-yellow-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {stats.pending_orders} Pending Orders
            </Badge>
          )}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          {kpiCards.map((card) => (
            <Card key={card.title} className="bg-stone-900 border-stone-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-stone-400">{card.title}</p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {loading ? '...' : card.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${card.bgColor}`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <Card className="lg:col-span-2 bg-stone-900 border-stone-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                Monthly Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]" data-testid="revenue-chart">
                {stats?.monthly_revenue && (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.monthly_revenue}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                      <XAxis dataKey="month" stroke="#78716c" />
                      <YAxis stroke="#78716c" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1c1917',
                          border: '1px solid #292524',
                          borderRadius: '0',
                        }}
                        labelStyle={{ color: '#fff' }}
                      />
                      <Bar dataKey="revenue" fill="#34d399" radius={[0, 0, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card className="bg-stone-900 border-stone-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-blue-400" />
                Recent Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4" data-testid="recent-orders">
                {loading ? (
                  <p className="text-stone-500 text-center py-4">Loading...</p>
                ) : stats?.recent_orders?.length === 0 ? (
                  <p className="text-stone-500 text-center py-4">No orders yet</p>
                ) : (
                  stats?.recent_orders?.map((order, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-3 border-b border-stone-800 last:border-0"
                    >
                      <div>
                        <p className="font-mono text-sm text-stone-300">{order.order_number}</p>
                        <p className="text-emerald-400 font-bold">
                          KES {order.total.toLocaleString()}
                        </p>
                      </div>
                      <Badge className={statusColors[order.status] || 'bg-stone-700 text-stone-300'}>
                        {order.status}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
